// src/pages/Subscription/index.js
import React, { useEffect, useMemo, useState } from "react";
import { api, urls } from "../../api/api";
import { Container, Row, Col, Card, CardBody, Button, Badge, Alert } from "reactstrap";

const badgeColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (["active", "trialing", "trial", "paid"].includes(s)) return "success";
  if (["past_due", "grace", "warning"].includes(s)) return "warning";
  if (["canceled", "cancelled", "expired", "inactive"].includes(s)) return "danger";
  return "secondary";
};

const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
};

const daysLeft = (endAt) => {
  if (!endAt) return null;
  const d = new Date(endAt);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default function Subscription() {
  document.title = "Subscription";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(urls.subscription);
      setData(r.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || "Erreur chargement subscription");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const summary = useMemo(() => {
    // نخدمو بأي shape يجي من backend
    const company = data?.company || data?.data?.company || null;
    const plan = data?.plan || data?.data?.plan || null;
    const sub = data?.subscription || data?.data?.subscription || data?.sub || null;

    const status = sub?.status ?? data?.status ?? plan?.status ?? "unknown";
    const isActive = sub?.is_active ?? data?.is_active ?? null;
    const startsAt = sub?.starts_at ?? data?.starts_at ?? null;
    const endsAt = sub?.ends_at ?? sub?.end_at ?? data?.ends_at ?? plan?.ends_at ?? null;

    const planName = plan?.name ?? sub?.plan_name ?? data?.plan_name ?? "—";
    const price = plan?.price ?? plan?.price_mad ?? plan?.amount ?? null;
    const period = plan?.period ?? plan?.interval ?? plan?.billing_period ?? null;

    return {
      companyName: company?.name ?? data?.company_name ?? "—",
      planName,
      status,
      isActive,
      startsAt,
      endsAt,
      price,
      period,
    };
  }, [data]);

  const left = daysLeft(summary.endsAt);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Subscription</h4>
            <Button color="secondary" onClick={load} disabled={loading}>
              {loading ? "..." : "Refresh"}
            </Button>
          </Col>
        </Row>

        {err ? <Alert color="danger">{err}</Alert> : null}

        <Row className="g-3">
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Company
                    </div>
                    <div className="h5 mb-0">{summary.companyName}</div>
                  </div>

                  <div className="text-end">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Status
                    </div>
                    <Badge color={badgeColor(summary.status)} pill style={{ fontSize: 12 }}>
                      {String(summary.status || "unknown")}
                    </Badge>
                  </div>
                </div>

                <hr />

                <div className="d-flex justify-content-between">
                  <div className="text-muted">Plan</div>
                  <div className="fw-semibold">{summary.planName}</div>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <div className="text-muted">Active</div>
                  <div className="fw-semibold">
                    {summary.isActive === null ? "-" : summary.isActive ? "Yes" : "No"}
                  </div>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <div className="text-muted">Start</div>
                  <div className="fw-semibold">{fmtDateTime(summary.startsAt)}</div>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <div className="text-muted">End</div>
                  <div className="fw-semibold">{fmtDateTime(summary.endsAt)}</div>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <div className="text-muted">Days left</div>
                  <div className="fw-semibold">
                    {left === null ? "-" : left > 0 ? `${left} days` : "Expired"}
                  </div>
                </div>

                {summary.price !== null ? (
                  <div className="d-flex justify-content-between mt-2">
                    <div className="text-muted">Price</div>
                    <div className="fw-semibold">
                      {summary.price}
                      {summary.period ? ` / ${summary.period}` : ""}
                    </div>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
